import { isPlatformBrowser } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  PLATFORM_ID,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Tracking } from '@freelancer/tracking';
import { trackByIndex } from '@freelancer/ui';
import type { VideoComponent } from '@freelancer/ui/video';
import { VideoObjectFit } from '@freelancer/ui/video';

@Component({
  standalone: false,
  selector: 'app-video-hero',
  template: `
    @for (src of videoSrc; track trackByIndex(i); let i = $index) {
      @if (loadedVideos.length > i - 1) {
        <fl-video
          #videoComponent
          class="HeroVideo"
          [class.Loading]="i === 0 && loadedVideos.length === 0"
          [hidden]="currentIndex !== i"
          [disableControls]="true"
          [objectFit]="VideoObjectFit.COVER"
          [isExternal]="false"
          [src]="src"
          [loadTimeout]="loadTimeout"
          (canPlayThrough)="videoLoaded(i)"
          (error)="detectedError()"
          (videoEnd)="videoEnded()"
        >
        </fl-video>
      }
    }
  `,
  styleUrls: ['./video-hero.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoHeroComponent implements OnInit {
  VideoObjectFit = VideoObjectFit;
  currentIndex = 0;
  numberofVideos = 4;
  loadTimeout = 10_000;
  trackByIndex = trackByIndex;
  startTime: number;

  @ViewChildren('videoComponent') videoComponents: QueryList<VideoComponent>;

  @Input() videoSrc: readonly string[];

  @Output() indexChanged = new EventEmitter<number>();
  @Output() errorDetected = new EventEmitter<void>();
  loadedVideos: readonly VideoComponent[] = [];

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private tracking: Tracking,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.startTime = Date.now();
    }
  }

  /**
   * Will only trigger `errorDetected` if no video was able to load.
   * If any video has been loaded before the error will used videos to loop
   */
  detectedError(): void {
    if (this.loadedVideos.length === 0) {
      this.triggerEventTracking('HeroVideoError');
      this.errorDetected.emit();
    }
  }

  // This will add to the list of already loaded video tags
  videoLoaded(index: number): void {
    if (this.loadedVideos[index] === undefined) {
      const videoComponent = this.videoComponents.toArray()[index];
      if (index === 0) {
        videoComponent.play().then(response => {
          if (response.status === 'success') {
            this.loadedVideos = [videoComponent];
            this.triggerEventTracking('HeroVideoPlays');
            this.changeDetectorRef.markForCheck();
          } else if (response.status === 'error') {
            this.detectedError();
          }
        });
      } else {
        this.loadedVideos = [...this.loadedVideos, videoComponent];
        this.changeDetectorRef.markForCheck();
      }
    }
  }

  // This will determine what video will play next once the previous video has finished
  videoEnded(): void {
    this.currentIndex++;
    if (
      this.currentIndex >= this.numberofVideos ||
      this.currentIndex >= this.loadedVideos.length
    ) {
      this.currentIndex = 0;
    }
    this.changeDetectorRef.markForCheck();

    this.loadedVideos[this.currentIndex].play().then(response => {
      if (response.status === 'success') {
        this.indexChanged.emit(this.currentIndex);
      } else if (response.status === 'error') {
        this.errorDetected.emit();
      }
    });
  }

  triggerEventTracking(label: string): void {
    this.tracking.trackCustomEvent(label, 'HomePage', {
      loadtime: Date.now() - this.startTime,
    });
  }
}
